import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, Property } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import PropertiesContext from "../../contexts/PropertiesContext";
import useProperties from "../../hooks/data/useProperties";

function requiredPropertyId(row: Property): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Property is missing its ID.");
    }

    return row.id;
}

const usePropertyColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<Property>[] = [
            {
                id: "name",
                label: "Name",
                scalarType: "text",
                accessor: (row) => row.name,
                flex: 1,
            },
            {
                id: "rank",
                label: "Rank",
                scalarType: "number",
                accessor: (row) => row.rank,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/lot-properties/edit/${requiredPropertyId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Properties: FC = () => {
    const columns = usePropertyColumns();

    const theme = useTheme();

    const context = useProperties();

    const { data: properties, loading: propertiesLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Lot Properties`,
            exportTitle: `Lot Properties`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <PropertiesContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/lot-properties/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Property
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Properties"
                        loading={propertiesLoading}
                        rows={properties ?? []}
                        columns={columns}
                        getRowId={requiredPropertyId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </PropertiesContext.Provider>
    );
};

export default Properties;
